import React, { useState, useEffect, useMemo } from "react";
import PageLayout from "../../components/layoutComponents/PageLayout";
import MazhrModal from "../../components/reusableElementsComponents/MazhrModal";
import Switch from "@mui/material/Switch";
import checkGreen from "../../assets/images/icons/checkGreen.svg";
import lockIcon from "../../assets/images/icons/lock.svg";
import TalentsApiService from "../../TalentsApiService";
import { t, get, lookups } from "../../content";
import { useLocale } from "../../locale";

function DataShareInvites() {
  const { locale } = useLocale();
  const [teamData, setTeamData] = useState([]);
  const [expandedTeamIndex, setExpandedTeamIndex] = useState(0);
  const [chosenTeamId, setChosenTeamId] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogContent, setDialogContent] = useState(null);
  const [dialogRejectReasons, setDialogRejectReasons] = useState([]);

  const talentsServiceObj = useMemo(() => {
    return new TalentsApiService();
  }, []);

  const dialogCopy = get("dataShare.dialog") || {};
  const statusOption = get("dataShare.statusOption") || {};

  useEffect(() => {
    talentsServiceObj.FetchDataShareInvites(setTeamData);
    // eslint-disable-next-line
  }, []);

  const handleRowClick = (e, index) => {
    e.stopPropagation();
    setExpandedTeamIndex(index);
  };

  const handleToggleClick = (e, teamId, currentDataAccess) => {
    e.stopPropagation();
    setOpenDialog(true);
    setDialogContent(
      currentDataAccess ? dialogCopy.reject : dialogCopy.accept
    );
    setDialogRejectReasons(
      (dialogCopy.rejectReasons || []).map((i) => ({
        ...i,
        isSelected: false,
      }))
    );
    setChosenTeamId(teamId);
  };

  const handleDialogCheckboxChange = (e, index) => {
    e.stopPropagation();
    const newDialogRejectReasons = dialogRejectReasons?.map((label, i) => {
      if (i === index) {
        return {
          ...label,
          isSelected: !label.isSelected,
        };
      }
      return label;
    });
    setDialogRejectReasons(newDialogRejectReasons);
  };

  const handleInvitationAction = (e, isAccepted) => {
    e.stopPropagation();
  
    const targetTeam = teamData?.[chosenTeamId];
    const invitation_type = targetTeam?.type === "team_invite" ? "team" : "job";
    const status = isAccepted ? 2 : 3;
    const selectedRejectReasons = dialogRejectReasons?.filter(
      (reason) => reason.isSelected
    ).map((reason) => parseInt(Number(reason.id), 10)) || [];

    talentsServiceObj.ToggleDataShareInviteStatus(invitation_type, status, selectedRejectReasons || [], targetTeam.hash, 
      () => talentsServiceObj.FetchDataShareInvites(setTeamData));

    setOpenDialog(false);
    setDialogRejectReasons(
      dialogRejectReasons?.map((label) => ({ ...label, isSelected: false }))
    );
  };

  const assessmentLabel = (assessment) => {
    const fromCatalog = lookups.assessments?.[String(assessment.id || assessment.instrument_id)]?.name;
    return fromCatalog || assessment.label;
  };

  const renderTeamContent = (team) => {
    if (!team) return null;
    return (
      <div className="high-light">
        {team.team ? (
          <p>
            {t("dataShare.areYouEmployedBy")}{" "}
            <span className="semi-bold">{team.company}</span>{" "}
            {t("dataShare.andWorkInTeam")}{" "}
            <span className="semi-bold">{team.team}</span>?
          </p>
        ) : team.job ? (
          <p>
            {t("dataShare.areYouApplyingToJob")}{" "}
            <span className="semi-bold">{team.job}</span>{" "}
            {t("dataShare.inCompany")}{" "}
            <span className="semi-bold">{team.company}</span>?
          </p>
        ) : (
          ""
        )}

        <p className="semi-bold">
          {t("dataShare.requestedAssessments")}:
        </p>
        <ul>
          {team?.requested_assessments.map((assessment, index) => (
            <li key={index} className="high-light-list-item">
              {assessment.completed ? (
                <img src={checkGreen} alt="messages-page-assessment-check" />
              ) : (
                <img src={lockIcon} alt="messages-page-assessment-lock" />
              )}

              {assessmentLabel(assessment)}
            </li>
          ))}
        </ul>
        <p>
          <span className="semi-bold">{t("dataShare.about")}: </span>
          {team?.about}
        </p>
        <p></p>
      </div>
    );
  };

  return (
    <div className="bg-cover" key={locale}>
      <PageLayout>
        <div className="messages-page-wrapper">
          <h1>{t("dataShare.title")}</h1>
        </div>
      </PageLayout>

      <PageLayout>
        <div className="messages-page-wrapper">
          <div className="messages-page-section team-list">
            <div className="team-list-row team-list-head">
              <div>{t("dataShare.invitation")}</div>
              <div className="team-list-filter">{t("dataShare.status")}</div>
              <div className="team-list-filter">
                {t("dataShare.dataAccess")}
              </div>
            </div>
            <div>
              {teamData.map((team, index) => (
                <React.Fragment key={index}>
                  <div
                    onClick={(e) => handleRowClick(e, index)}
                    className={`team-list-row team-list-body ${
                      expandedTeamIndex === index ? "expanded" : ""
                    } ${expandedTeamIndex === index ? "desktop-focus" : ""}`}
                  >
                    <div>
                      <p>
                        {team.team
                          ? `${t("dataShare.team")}: ${team.team}`
                          : team.job
                          ? `${t("dataShare.job")}: ${team.job}`
                          : ""}
                      </p>
                      <p>{team.company}</p>
                    </div>
                    <div className="team-list-filter">
                      {statusOption[team.status] || ""}
                    </div>
                    <div className="team-list-filter">
                      <Switch
                        checked={team.dataAccess ? true : false}
                        onChange={(e) =>
                          handleToggleClick(e, team.id, team.dataAccess)
                        }
                        inputProps={{ "aria-label": "condivolled" }}
                      />
                    </div>
                  </div>
                  {index === expandedTeamIndex && (
                    <div className="expanded-row">
                      <div colSpan="4">
                        {renderTeamContent(team)}
                      </div>
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
          <div className="messages-page-section about-team">
            <div className="team-list-row team-list-head"></div>
            {teamData &&
              teamData.length > 0 &&
              renderTeamContent(teamData[expandedTeamIndex])}
          </div>
        </div>
      </PageLayout>

      <MazhrModal
        show={openDialog}
        onHide={() => setOpenDialog(false)}
        header={<h5 className="">{dialogContent && dialogContent.header}</h5>}
      >
        {dialogContent && (
          <div className="messages-page-dialog-wrapper">
            <p className="">{dialogContent.content}</p>
            <div className="">
              {
                !dialogContent.isAccepted &&
                  dialogRejectReasons?.map((label, index) => (
                    <label
                      htmlFor={`messagesPageDialogLabel${index}`}
                      className="dialog-list-item"
                      key={index}
                    >
                      <input
                        type="checkbox"
                        name={label.id}
                        checked={label.isSelected}
                        onChange={(e) => handleDialogCheckboxChange(e, index)}
                        className="list-item-checkbox"
                      />
                      <span>{label.label}</span>
                    </label>
                  ))
              }
            </div>
            <button
              className={`mazhrBtn w-100 dialog-button ${
                dialogContent.isAccepted ? "accept" : "reject"
              }`}
              onClick={(e) =>
                handleInvitationAction(e, dialogContent.isAccepted)
              }
            >
              {dialogContent.buttonLabel}
            </button>
          </div>
        )}
      </MazhrModal>
    </div>
  );
}

export default DataShareInvites;
